import { ReactNode, useState, useMemo, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  AgreementContext,
  AgreementContextType,
  CommentItem,
} from "./AgreementContext";
import { fetchAgreement } from "@/services/agreements";
import { AgreementConfirmDialog } from "../components/shared/AgreementConfirmDialog";
import {
  useAgreementHandlers,
  useAgreementLoading,
  useAgreementError,
  useAgreementTransition,
  useAgreementDialog,
} from "../hooks";
import { Module } from "@/constants";

interface AgreementProviderProps {
  children: ReactNode;
  agreementId: string;
}

export const AgreementProvider = ({
  children,
  agreementId,
}: AgreementProviderProps) => {
  const [commentTexts, setCommentTexts] = useState<CommentItem[]>(() => []);

  const agreementQuery = useQuery({
    queryKey: [Module.Agreements, agreementId],
    queryFn: () => fetchAgreement(agreementId),
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  const { data } = agreementQuery;

  const { dialogHandlerKey, openDialog, closeDialog, confirmDialog } =
    useAgreementDialog();

  const clearCommentDrafts = useCallback(() => {
    setCommentTexts([]);
  }, []);

  const hasValidComments = useMemo(() => {
    return commentTexts.some((comment) => comment.text.trim().length > 0);
  }, [commentTexts]);
  const hasDraftComments = hasValidComments;

  const {
    handleSendQuestionnaire,
    handleComplete,
    handleReject,
    handleConfirm,
    handleComment,
    handleUpdateContent,
    handleUpdateData,
    handleUpdateStatements,
    handleUpdateTaxStatus,
    handleDelete,
    clearError,
    mutations,
  } = useAgreementHandlers({
    agreementId,
    commentTexts,
    openDialog,
    closeDialog,
    onCommentSuccess: clearCommentDrafts,
  });

  const queries = { agreementQuery };

  const loadingState = useAgreementLoading(queries, mutations);

  const errorState = useAgreementError(queries, mutations);
  const transitions = useAgreementTransition({ data });

  const refreshAgreement = useCallback(() => {
    agreementQuery.refetch();
  }, [agreementQuery]);

  const contextValue: AgreementContextType = useMemo(
    () => ({
      data,
      errorState,
      loadingState,
      comments: commentTexts,
      setComments: setCommentTexts,
      transitions,
      hasValidComments,
      handleSendQuestionnaire,
      handleComplete,
      handleReject,
      handleConfirm,
      handleComment,
      handleUpdateContent,
      handleUpdateData,
      handleUpdateStatements,
      handleUpdateTaxStatus,
      handleDelete,
      clearError,
      refreshAgreement,
    }),
    [
      data,
      loadingState,
      errorState,
      commentTexts,
      transitions,
      hasValidComments,
      handleSendQuestionnaire,
      handleComplete,
      handleReject,
      handleConfirm,
      handleComment,
      handleUpdateContent,
      handleUpdateData,
      handleUpdateStatements,
      handleUpdateTaxStatus,
      handleDelete,
      clearError,
      refreshAgreement,
    ],
  );

  return (
    <AgreementContext.Provider value={contextValue}>
      {children}
      <AgreementConfirmDialog
        handlerKey={dialogHandlerKey}
        onConfirm={confirmDialog}
        onCancel={closeDialog}
        hasDraftComments={hasDraftComments}
      />
    </AgreementContext.Provider>
  );
};
